import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Row,
    Select,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
} from "antd";

import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    ReloadOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../../api/api";

const { Text } = Typography;

const PAGE_SIZE = 100;

const STATUS_OPTIONS = [
    {
        value: "all",
        label: "All Courses",
    },
    {
        value: "pending",
        label: "Pending / Verifying",
    },
    {
        value: "approved",
        label: "Approved",
    },
];

function ApprovedLearningCourse() {
    const [courses, setCourses] = useState([]);

    const [loading, setLoading] =
        useState(true);

    const [refreshing, setRefreshing] =
        useState(false);

    const [approving, setApproving] =
        useState(false);

    const [search, setSearch] =
        useState("");

    const [statusFilter, setStatusFilter] =
        useState("all");

    const [selectedRowKeys, setSelectedRowKeys] =
        useState([]);

    const [errorMessage, setErrorMessage] =
        useState("");

    const getErrorMessage = useCallback(
        (error, fallback) => {
            const data =
                error?.response?.data;

            if (
                typeof data?.detail ===
                "string"
            ) {
                return data.detail;
            }

            if (
                typeof data?.message ===
                "string"
            ) {
                return data.message;
            }

            if (
                data &&
                typeof data === "object"
            ) {
                const firstValue =
                    Object.values(data)[0];

                if (
                    Array.isArray(firstValue) &&
                    firstValue.length
                ) {
                    return String(
                        firstValue[0]
                    );
                }

                if (
                    typeof firstValue ===
                    "string"
                ) {
                    return firstValue;
                }
            }

            return (
                error?.message ||
                fallback
            );
        },
        []
    );

    const formatDateTime = useCallback(
        (value) => {
            if (!value) {
                return "Not available";
            }

            const date =
                new Date(value);

            if (
                Number.isNaN(
                    date.getTime()
                )
            ) {
                return "Date unavailable";
            }

            return new Intl.DateTimeFormat(
                "en-LR",
                {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone:
                        "Africa/Monrovia",
                }
            ).format(date);
        },
        []
    );

    const formatCurrency = useCallback(
        (value, currency = "LRD") => {
            const amount =
                Number(value);

            if (
                Number.isNaN(amount)
            ) {
                return `0.00 ${currency}`;
            }

            return `${amount.toLocaleString(
                "en-LR",
                {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }
            )} ${currency}`;
        },
        []
    );

    const getTrainingYear = useCallback(
        (record) => {
            return (
                record?.academic_year ||
                record?.academic_year_name ||
                record?.training_year ||
                "—"
            );
        },
        []
    );

    const getStudentName = useCallback(
        (record) => {
            if (
                record?.student_name &&
                String(
                    record.student_name
                ).trim()
            ) {
                return String(
                    record.student_name
                ).trim();
            }

            if (
                record?.student_username
            ) {
                return record.student_username;
            }

            if (
                record?.username
            ) {
                return record.username;
            }

            return "Unknown Student";
        },
        []
    );

    const getStudentUsername =
        useCallback(
            (record) => {
                return (
                    record?.student_username ||
                    record?.username ||
                    ""
                );
            },
            []
        );

    const getEnrollmentId = useCallback(
        (record) => {
            const value =
                record?.enrollment_id ??
                record?.id;

            const id = Number(value);

            return Number.isInteger(id) &&
                id > 0
                ? id
                : null;
        },
        []
    );

    const getRowKey = useCallback(
        (record) => {
            const enrollmentId =
                getEnrollmentId(record);

            if (enrollmentId) {
                return `enrollment-${enrollmentId}`;
            }

            if (record?.order_item_id) {
                return `item-${record.order_item_id}`;
            }

            return `course-${record?.course_id || "unknown"}-${
                record?.order_id || "unknown"
            }`;
        },
        [getEnrollmentId]
    );

    const getStatusTag = useCallback(
        (record) => {
            if (
                record?.learning_status ===
                "approved"
            ) {
                return (
                    <Tag
                        icon={
                            <CheckCircleOutlined />
                        }
                        color="success"
                    >
                        Approved
                    </Tag>
                );
            }

            return (
                <Tag
                    icon={
                        <ClockCircleOutlined />
                    }
                    color="warning"
                >
                    Pending / Verifying
                </Tag>
            );
        },
        []
    );

    const fetchCourses = useCallback(
        async (
            showRefresh = false
        ) => {
            if (showRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            setErrorMessage("");

            try {
                const response =
                    await api.get(
                        "/api/course-learning/admin/learning-courses/",
                        {
                            params: {
                                page_size:
                                    PAGE_SIZE,
                            },
                        }
                    );

                const responseData =
                    response?.data || {};

                let results = [];

                if (
                    Array.isArray(
                        responseData
                    )
                ) {
                    results = responseData;
                } else if (
                    Array.isArray(
                        responseData.results
                    )
                ) {
                    results =
                        responseData.results;
                } else if (
                    Array.isArray(
                        responseData.items
                    )
                ) {
                    results =
                        responseData.items;
                }

                setCourses(results);

                setSelectedRowKeys(
                    []
                );
            } catch (error) {
                setErrorMessage(
                    getErrorMessage(
                        error,
                        "Unable to load the course approval records."
                    )
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [getErrorMessage]
    );

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const filteredCourses = useMemo(() => {
        const normalizedSearch =
            search
                .trim()
                .toLowerCase();

        return courses.filter(
            (course) => {
                const matchesStatus =
                    statusFilter ===
                        "all" ||
                    course.learning_status ===
                        statusFilter;

                if (!matchesStatus) {
                    return false;
                }

                if (
                    !normalizedSearch
                ) {
                    return true;
                }

                const searchableText = [
                    course.student_name,
                    course.student_username,
                    course.username,
                    course.course_name,
                    course.facilitator_name,
                    getTrainingYear(
                        course
                    ),
                    course.reference_number,
                    course.order_id,
                    course.order_item_id,
                    course.payment_method,
                    course.learning_status,
                    course.academic_year,
                ]
                    .filter(
                        (value) =>
                            value !==
                                null &&
                            value !==
                                undefined
                    )
                    .join(" ")
                    .toLowerCase();

                return searchableText.includes(
                    normalizedSearch
                );
            }
        );
    }, [
        courses,
        search,
        statusFilter,
        getTrainingYear,
    ]);

    const pendingCount = useMemo(
        () =>
            courses.filter(
                (course) =>
                    course.learning_status ===
                    "pending"
            ).length,
        [courses]
    );

    const approvedCount = useMemo(
        () =>
            courses.filter(
                (course) =>
                    course.learning_status ===
                    "approved"
            ).length,
        [courses]
    );

    const studentCount = useMemo(() => {
        return new Set(
            courses
                .map(
                    (course) =>
                        course.student_id
                )
                .filter(
                    (id) =>
                        id !== null &&
                        id !== undefined
                )
        ).size;
    }, [courses]);

    const selectedPendingRecords =
        useMemo(() => {
            return courses.filter(
                (course) => {
                    const rowKey =
                        getRowKey(course);

                    return (
                        selectedRowKeys.includes(
                            rowKey
                        ) &&
                        course.learning_status ===
                            "pending"
                    );
                }
            );
        }, [
            courses,
            selectedRowKeys,
            getRowKey,
        ]);

    const selectedPendingIds =
        useMemo(() => {
            return selectedPendingRecords
                .map(
                    (record) =>
                        getEnrollmentId(
                            record
                        )
                )
                .filter(
                    (id) =>
                        id !== null
                );
        }, [
            selectedPendingRecords,
            getEnrollmentId,
        ]);

    const approveSingle = async (
        record
    ) => {
        if (
            !record ||
            record.learning_status ===
                "approved"
        ) {
            return;
        }

        const enrollmentId =
            getEnrollmentId(record);

        if (!enrollmentId) {
            await Swal.fire({
                icon: "error",
                title: "Enrollment Not Found",
                text:
                    "This course does not have a valid enrollment record for approval.",
                confirmButtonColor:
                    "#062A4B",
            });

            return;
        }

        const studentName =
            getStudentName(record);

        const result =
            await Swal.fire({
                icon: "question",
                title: "Approve Course?",
                html: `
                    <div style="line-height:1.7;text-align:left;">
                        <div style="margin-bottom:8px;">
                            <strong>Student:</strong>
                            ${studentName}
                        </div>

                        <div style="margin-bottom:8px;">
                            <strong>Course:</strong>
                            ${
                                record.course_name ||
                                "Course"
                            }
                        </div>

                        <div style="margin-bottom:8px;">
                            <strong>Training Year:</strong>
                            ${getTrainingYear(record)}
                        </div>

                        <div style="margin-bottom:8px;">
                            <strong>Course Amount:</strong>
                            ${formatCurrency(
                                record.unit_price,
                                record.currency ||
                                    "LRD"
                            )}
                        </div>

                        ${
                            record.order_id
                                ? `
                                    <div style="margin-bottom:8px;">
                                        <strong>Order:</strong>
                                        #${record.order_id}
                                    </div>
                                `
                                : ""
                        }

                        <div style="margin-top:12px;color:#647482;">
                            Approving this course will allow the
                            student to access the learning environment.
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText:
                    "Approve Course",
                cancelButtonText:
                    "Cancel",
                confirmButtonColor:
                    "#16805C",
                cancelButtonColor:
                    "#647482",
                background: "#FFFFFF",
                color: "#172B3A",
            });

        if (!result.isConfirmed) {
            return;
        }

        setApproving(true);

        try {
            const response =
                await api.post(
                    "/api/course-learning/admin/learning-courses/approve/",
                    {
                        enrollment_ids: [
                            enrollmentId,
                        ],
                    }
                );

            const approvedCount =
                Number(
                    response?.data
                        ?.approved_count
                ) || 0;

            if (approvedCount !== 1) {
                throw new Error(
                    "The server did not approve this enrollment."
                );
            }

            setCourses(
                (current) =>
                    current.map(
                        (item) =>
                            getEnrollmentId(
                                item
                            ) ===
                            enrollmentId
                                ? {
                                      ...item,
                                      learning_status:
                                          "approved",
                                      learning_status_label:
                                          "Approved",
                                      is_approved:
                                          true,
                                      can_learn:
                                          true,
                                      approved_at:
                                          new Date().toISOString(),
                                  }
                                : item
                    )
            );

            setSelectedRowKeys(
                (current) =>
                    current.filter(
                        (key) => {
                            return (
                                key !==
                                getRowKey(
                                    record
                                )
                            );
                        }
                    )
            );

            await Swal.fire({
                icon: "success",
                title: "Course Approved",
                html: `
                    <div style="line-height:1.7;">
                        <strong>
                            ${
                                record.course_name ||
                                "Course"
                            }
                        </strong>
                        is now approved for
                        <strong>
                            ${studentName}
                        </strong>.
                    </div>
                `,
                confirmButtonColor:
                    "#062A4B",
                background: "#FFFFFF",
                color: "#172B3A",
            });

            await fetchCourses(true);
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Approval Failed",
                text: getErrorMessage(
                    error,
                    "The course could not be approved. Please try again."
                ),
                confirmButtonColor:
                    "#062A4B",
            });
        } finally {
            setApproving(false);
        }
    };

    const approveSelected = async () => {
        if (
            !selectedPendingIds.length
        ) {
            await Swal.fire({
                icon: "info",
                title: "No Pending Courses Selected",
                text:
                    "Select one or more pending courses before approving.",
                confirmButtonColor:
                    "#062A4B",
            });

            return;
        }

        const studentGroups =
            new Set(
                selectedPendingRecords.map(
                    (course) =>
                        getStudentName(
                            course
                        )
                )
            );

        const result =
            await Swal.fire({
                icon: "question",
                title: "Approve Selected Courses?",
                html: `
                    <div style="line-height:1.7;">
                        You are about to approve
                        <strong>
                            ${selectedPendingIds.length}
                        </strong>
                        course
                        ${
                            selectedPendingIds.length ===
                            1
                                ? ""
                                : "s"
                        }
                        for
                        <strong>
                            ${studentGroups.size}
                        </strong>
                        student
                        ${
                            studentGroups.size ===
                            1
                                ? ""
                                : "s"
                        }.
                        <br />
                        <span style="color:#647482;">
                            Approved students will immediately
                            be permitted to access their courses.
                        </span>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText:
                    "Approve Selected",
                cancelButtonText:
                    "Cancel",
                confirmButtonColor:
                    "#16805C",
                cancelButtonColor:
                    "#647482",
                background: "#FFFFFF",
                color: "#172B3A",
            });

        if (!result.isConfirmed) {
            return;
        }

        setApproving(true);

        try {
            const response =
                await api.post(
                    "/api/course-learning/admin/learning-courses/approve/",
                    {
                        enrollment_ids:
                            selectedPendingIds,
                    }
                );

            const approvedCount =
                Number(
                    response?.data
                        ?.approved_count
                ) || 0;

            if (
                approvedCount !==
                selectedPendingIds.length
            ) {
                throw new Error(
                    `The server approved ${approvedCount} of ${selectedPendingIds.length} selected courses.`
                );
            }

            setCourses(
                (current) =>
                    current.map(
                        (item) => {
                            const enrollmentId =
                                getEnrollmentId(
                                    item
                                );

                            return selectedPendingIds.includes(
                                enrollmentId
                            )
                                ? {
                                      ...item,
                                      learning_status:
                                          "approved",
                                      learning_status_label:
                                          "Approved",
                                      is_approved:
                                          true,
                                      can_learn:
                                          true,
                                      approved_at:
                                          new Date().toISOString(),
                                  }
                                : item;
                        }
                    )
            );

            setSelectedRowKeys([]);

            await Swal.fire({
                icon: "success",
                title: "Courses Approved",
                text:
                    `${selectedPendingIds.length} course${
                        selectedPendingIds.length ===
                        1
                            ? ""
                            : "s"
                    } approved successfully.`,
                timer: 1900,
                showConfirmButton: false,
                background: "#FFFFFF",
                color: "#172B3A",
            });

            await fetchCourses(true);
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Bulk Approval Failed",
                text: getErrorMessage(
                    error,
                    "The selected courses could not be approved."
                ),
                confirmButtonColor:
                    "#062A4B",
            });
        } finally {
            setApproving(false);
        }
    };

    const columns = [
        {
            title: "Student",
            key: "student",
            width: 220,
            sorter: (a, b) =>
                getStudentName(
                    a
                ).localeCompare(
                    getStudentName(
                        b
                    )
                ),
            render: (_, record) => {
                const username =
                    getStudentUsername(
                        record
                    );

                return (
                    <div className="cayep-approval-student">
                        <Avatar
                            size={42}
                            icon={
                                <UserOutlined />
                            }
                            className="cayep-approval-avatar"
                        />

                        <div>
                            <strong>
                                {getStudentName(
                                    record
                                )}
                            </strong>

                            {username &&
                                username !==
                                    getStudentName(
                                        record
                                    ) && (
                                    <span>
                                        @
                                        {
                                            username
                                        }
                                    </span>
                                )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Course",
            key: "course",
            width: 280,
            render: (_, record) => (
                <div className="cayep-approval-course">
                    <strong>
                        {record.course_name ||
                            "Unnamed Course"}
                    </strong>

                    {record.facilitator_name && (
                        <span>
                            Facilitator:{" "}
                            {
                                record.facilitator_name
                            }
                        </span>
                    )}

                    {record.order_item_id && (
                        <span>
                            Item #
                            {
                                record.order_item_id
                            }
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Training Year",
            key: "academic_year",
            width: 145,
            render: (_, record) =>
                getTrainingYear(record),
        },
        {
            title: "Payment",
            key: "payment",
            width: 190,
            render: (_, record) => (
                <div className="cayep-approval-payment">
                    <strong>
                        {record.payment_method
                            ? record.payment_method
                                  .replace(
                                      /_/g,
                                      " "
                                  )
                                  .replace(
                                      /\b\w/g,
                                      (letter) =>
                                          letter.toUpperCase()
                                  )
                            : "—"}
                    </strong>

                    <span>
                        Course:{" "}
                        {formatCurrency(
                            record.unit_price,
                            record.currency ||
                                "LRD"
                        )}
                    </span>

                    {record.reference_number && (
                        <span>
                            Ref:{" "}
                            {
                                record.reference_number
                            }
                        </span>
                    )}

                    {record.order_id && (
                        <span>
                            Order #
                            {
                                record.order_id
                            }
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Submitted",
            dataIndex: "submitted_at",
            key: "submitted_at",
            width: 170,
            render: (
                value,
                record
            ) => (
                <span className="cayep-approval-date">
                    {formatDateTime(
                        value ||
                            record.enrolled_at ||
                            record.created_at
                    )}
                </span>
            ),
            sorter: (a, b) =>
                new Date(
                    a.submitted_at ||
                        a.enrolled_at ||
                        a.created_at ||
                        0
                ).getTime() -
                new Date(
                    b.submitted_at ||
                        b.enrolled_at ||
                        b.created_at ||
                        0
                ).getTime(),
        },
        {
            title: "Status",
            key: "status",
            width: 165,
            render: (_, record) =>
                getStatusTag(record),
        },
        {
            title: "Approved",
            dataIndex: "approved_at",
            key: "approved_at",
            width: 170,
            render: (
                value,
                record
            ) =>
                record.learning_status ===
                "approved" ? (
                    <div className="cayep-approval-date approved">
                        <CheckCircleOutlined />
                        <span>
                            {formatDateTime(
                                value
                            )}
                        </span>
                    </div>
                ) : (
                    <span className="cayep-not-approved">
                        Not approved
                    </span>
                ),
        },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            width: 130,
            render: (_, record) =>
                record.learning_status ===
                "approved" ? (
                    <Tag color="success">
                        Approved
                    </Tag>
                ) : (
                    <Button
                        type="primary"
                        size="small"
                        icon={
                            <CheckCircleOutlined />
                        }
                        loading={
                            approving
                        }
                        onClick={() =>
                            approveSingle(
                                record
                            )
                        }
                        className="cayep-approve-button"
                    >
                        Approve
                    </Button>
                ),
        },
    ];

    return (
        <>
            <style>{`
                .cayep-approval-page {
                    min-height: 100%;
                    padding: clamp(14px, 2vw, 28px);
                    background: #F4F7F9;
                    color: #172B3A;
                }

                .cayep-approval-page *,
                .cayep-approval-page *::before,
                .cayep-approval-page *::after {
                    box-sizing: border-box;
                }

                .cayep-approval-header {
                    margin-bottom: 22px;
                }

                .cayep-approval-header-main {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 18px;
                }

                .cayep-approval-kicker {
                    display: block;
                    margin-bottom: 5px;
                    color: #C0750D;
                    font-size: 10px;
                    font-weight: 900;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                }

                .cayep-approval-header h1 {
                    margin: 0;
                    color: #062A4B;
                    font-size: clamp(23px, 3vw, 34px);
                    font-weight: 900;
                    line-height: 1.15;
                }

                .cayep-approval-header p {
                    max-width: 850px;
                    margin: 8px 0 0;
                    color: #647482;
                    font-size: 13px;
                    line-height: 1.65;
                }

                .cayep-approval-refresh {
                    flex: 0 0 auto;
                }

                .cayep-approval-stat-card {
                    height: 100%;
                    border: 1px solid #DCE4E8 !important;
                    border-radius: 16px !important;
                    box-shadow: 0 7px 25px rgba(6,42,75,.055) !important;
                }

                .cayep-approval-stat-card .ant-card-body {
                    padding: 18px;
                }

                .cayep-approval-stat-icon {
                    width: 42px;
                    height: 42px;
                    margin-bottom: 11px;
                    display: grid;
                    place-items: center;
                    border-radius: 11px;
                    background: #EEF4F8;
                    color: #062A4B;
                    font-size: 18px;
                }

                .cayep-approval-stat-card.pending
                .cayep-approval-stat-icon {
                    background: #FFF7E7;
                    color: #C88719;
                }

                .cayep-approval-stat-card.approved
                .cayep-approval-stat-icon {
                    background: #EAF7F1;
                    color: #16805C;
                }

                .cayep-approval-stat-card.students
                .cayep-approval-stat-icon {
                    background: #EDF5FB;
                    color: #2778A8;
                }

                .cayep-approval-stat-card .ant-statistic-title {
                    color: #647482;
                    font-size: 11px;
                    font-weight: 700;
                }

                .cayep-approval-stat-card .ant-statistic-content {
                    color: #172B3A;
                    font-weight: 900;
                }

                .cayep-approval-toolbar {
                    margin-top: 20px;
                    padding: 15px;
                    background: #FFFFFF;
                    border: 1px solid #DCE4E8;
                    border-radius: 16px;
                    box-shadow: 0 7px 25px rgba(6,42,75,.045);
                }

                .cayep-approval-toolbar-inner {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .cayep-approval-search {
                    width: min(440px, 100%);
                }

                .cayep-approval-search .ant-input-affix-wrapper {
                    min-height: 42px;
                    border-radius: 10px;
                }

                .cayep-approval-filter {
                    min-width: 190px;
                }

                .cayep-approval-filter .ant-select-selector {
                    min-height: 42px !important;
                    border-radius: 10px !important;
                    align-items: center;
                }

                .cayep-approval-bulk {
                    margin-top: 12px;
                    padding: 11px 13px;
                    border-radius: 10px;
                    background: #F4F8FA;
                    border: 1px solid #DCE4E8;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                }

                .cayep-approval-table-card {
                    margin-top: 18px;
                    border: 1px solid #DCE4E8 !important;
                    border-radius: 16px !important;
                    overflow: hidden;
                    box-shadow: 0 8px 30px rgba(6,42,75,.055) !important;
                }

                .cayep-approval-table-card .ant-card-body {
                    padding: 0;
                }

                .cayep-approval-table .ant-table-thead > tr > th {
                    background: #F7F9FA !important;
                    color: #425466;
                    font-size: 10px;
                    font-weight: 900;
                    letter-spacing: .04em;
                    text-transform: uppercase;
                    white-space: nowrap;
                }

                .cayep-approval-table .ant-table-tbody > tr > td {
                    padding-top: 13px;
                    padding-bottom: 13px;
                    border-bottom-color: #EEF1F3;
                }

                .cayep-approval-table .ant-table-tbody > tr:hover > td {
                    background: #F8FBFC !important;
                }

                .cayep-approval-student {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 180px;
                }

                .cayep-approval-avatar {
                    flex: 0 0 auto;
                    background: #062A4B !important;
                    color: #F6C944 !important;
                }

                .cayep-approval-student > div:last-child,
                .cayep-approval-course,
                .cayep-approval-payment {
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }

                .cayep-approval-student strong,
                .cayep-approval-course strong,
                .cayep-approval-payment strong {
                    color: #172B3A;
                    font-size: 12px;
                    line-height: 1.35;
                }

                .cayep-approval-student span,
                .cayep-approval-course span,
                .cayep-approval-payment span {
                    color: #788895;
                    font-size: 10px;
                    line-height: 1.4;
                }

                .cayep-approval-course strong {
                    color: #062A4B;
                }

                .cayep-approval-date {
                    color: #526372;
                    font-size: 10px;
                    line-height: 1.5;
                }

                .cayep-approval-date.approved {
                    display: flex;
                    align-items: flex-start;
                    gap: 6px;
                    color: #16805C;
                    font-weight: 700;
                }

                .cayep-not-approved {
                    color: #A4AFB7;
                    font-size: 10px;
                }

                .cayep-approve-button {
                    background: #16805C !important;
                    border-color: #16805C !important;
                    border-radius: 8px !important;
                    font-size: 10px !important;
                    font-weight: 800;
                }

                .cayep-approval-mobile-list {
                    display: none;
                }

                .cayep-approval-mobile-card {
                    margin-bottom: 10px;
                    border: 1px solid #DCE4E8;
                    border-radius: 14px;
                    background: #FFFFFF;
                    overflow: hidden;
                }

                .cayep-approval-mobile-card-top {
                    padding: 13px;
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    border-bottom: 1px solid #EEF1F3;
                }

                .cayep-approval-mobile-card-top input {
                    width: 16px;
                    height: 16px;
                    margin-top: 11px;
                    flex: 0 0 auto;
                    accent-color: #16805C;
                }

                .cayep-approval-mobile-card-info {
                    flex: 1;
                    min-width: 0;
                }

                .cayep-approval-mobile-card-info strong {
                    display: block;
                    color: #062A4B;
                    font-size: 13px;
                    line-height: 1.4;
                }

                .cayep-approval-mobile-card-info span {
                    display: block;
                    margin-top: 3px;
                    color: #788895;
                    font-size: 10px;
                }

                .cayep-approval-mobile-course {
                    padding: 13px;
                }

                .cayep-approval-mobile-course-title {
                    margin-bottom: 10px;
                    color: #172B3A;
                    font-size: 13px;
                    font-weight: 900;
                    line-height: 1.45;
                }

                .cayep-approval-mobile-meta {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 9px;
                }

                .cayep-approval-mobile-meta-item {
                    min-width: 0;
                    padding: 9px;
                    border-radius: 9px;
                    background: #F7F9FA;
                }

                .cayep-approval-mobile-meta-item small {
                    display: block;
                    margin-bottom: 3px;
                    color: #84929D;
                    font-size: 8px;
                    font-weight: 900;
                    letter-spacing: .05em;
                    text-transform: uppercase;
                }

                .cayep-approval-mobile-meta-item span {
                    display: block;
                    color: #34495A;
                    font-size: 10px;
                    line-height: 1.4;
                    word-break: break-word;
                }

                .cayep-approval-mobile-actions {
                    padding: 12px 13px;
                    border-top: 1px solid #EEF1F3;
                }

                .cayep-approval-mobile-actions button {
                    width: 100%;
                }

                @media (max-width: 850px) {
                    .cayep-approval-header-main {
                        flex-direction: column;
                    }

                    .cayep-approval-refresh {
                        width: 100%;
                    }

                    .cayep-approval-refresh button {
                        width: 100%;
                    }

                    .cayep-approval-toolbar-inner {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .cayep-approval-search,
                    .cayep-approval-filter {
                        width: 100%;
                    }

                    .cayep-approval-table-card {
                        display: none;
                    }

                    .cayep-approval-mobile-list {
                        display: block;
                        margin-top: 18px;
                    }
                }

                @media (max-width: 560px) {
                    .cayep-approval-page {
                        padding: 10px;
                    }

                    .cayep-approval-header {
                        margin-bottom: 14px;
                    }

                    .cayep-approval-header h1 {
                        font-size: 23px;
                    }

                    .cayep-approval-header p {
                        font-size: 11px;
                    }

                    .cayep-approval-mobile-meta {
                        grid-template-columns: 1fr;
                    }

                    .cayep-approval-stat-card .ant-card-body {
                        padding: 13px;
                    }

                    .cayep-approval-stat-icon {
                        width: 36px;
                        height: 36px;
                        margin-bottom: 8px;
                    }

                    .cayep-approval-bulk {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .cayep-approval-bulk button {
                        width: 100%;
                    }
                }
            `}</style>

            <div className="cayep-approval-page">
                <div className="cayep-approval-header">
                    <div className="cayep-approval-header-main">
                        <div>
                            <span className="cayep-approval-kicker">
                                COURSE ACCESS MANAGEMENT
                            </span>

                            <h1>
                                Course Approval
                            </h1>

                            <p>
                                Review submitted
                                training courses,
                                verify payment
                                information, monitor
                                individual course
                                submissions, and
                                approve course access
                                for student learning.
                            </p>
                        </div>

                        <div className="cayep-approval-refresh">
                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                loading={
                                    refreshing
                                }
                                onClick={() =>
                                    fetchCourses(
                                        true
                                    )
                                }
                            >
                                Refresh Records
                            </Button>
                        </div>
                    </div>
                </div>

                {errorMessage && (
                    <Alert
                        type="error"
                        showIcon
                        message="Unable to load course approval records"
                        description={
                            errorMessage
                        }
                        closable
                        onClose={() =>
                            setErrorMessage(
                                ""
                            )
                        }
                        style={{
                            marginBottom: 18,
                            borderRadius: 12,
                        }}
                    />
                )}

                <Row
                    gutter={[
                        12,
                        12,
                    ]}
                >
                    <Col
                        xs={24}
                        sm={12}
                        lg={8}
                    >
                        <Card className="cayep-approval-stat-card">
                            <div className="cayep-approval-stat-icon">
                                <TeamOutlined />
                            </div>

                            <Statistic
                                title="Students"
                                value={
                                    studentCount
                                }
                            />
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        lg={8}
                    >
                        <Card className="cayep-approval-stat-card pending">
                            <div className="cayep-approval-stat-icon">
                                <ClockCircleOutlined />
                            </div>

                            <Statistic
                                title="Pending / Verifying"
                                value={
                                    pendingCount
                                }
                            />
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        lg={8}
                    >
                        <Card className="cayep-approval-stat-card approved">
                            <div className="cayep-approval-stat-icon">
                                <SafetyCertificateOutlined />
                            </div>

                            <Statistic
                                title="Approved Courses"
                                value={
                                    approvedCount
                                }
                            />
                        </Card>
                    </Col>
                </Row>

                <div className="cayep-approval-toolbar">
                    <div className="cayep-approval-toolbar-inner">
                        <Input
                            className="cayep-approval-search"
                            prefix={
                                <SearchOutlined />
                            }
                            value={search}
                            onChange={(
                                event
                            ) =>
                                setSearch(
                                    event
                                        .target
                                        .value
                                )
                            }
                            placeholder="Search student, course, facilitator, training year, order or reference..."
                            allowClear
                        />

                        <Select
                            className="cayep-approval-filter"
                            value={
                                statusFilter
                            }
                            options={
                                STATUS_OPTIONS
                            }
                            onChange={
                                setStatusFilter
                            }
                        />
                    </div>

                    {selectedPendingIds.length >
                        0 && (
                        <div className="cayep-approval-bulk">
                            <Text>
                                <strong>
                                    {
                                        selectedPendingIds.length
                                    }
                                </strong>{" "}
                                pending course
                                {selectedPendingIds.length ===
                                1
                                    ? ""
                                    : "s"}{" "}
                                selected
                            </Text>

                            <Button
                                type="primary"
                                icon={
                                    <CheckCircleOutlined />
                                }
                                loading={
                                    approving
                                }
                                onClick={
                                    approveSelected
                                }
                                style={{
                                    background:
                                        "#16805C",
                                    borderColor:
                                        "#16805C",
                                }}
                            >
                                Approve Selected
                            </Button>
                        </div>
                    )}
                </div>

                <Card className="cayep-approval-table-card">
                    <Table
                        className="cayep-approval-table"
                        rowKey={getRowKey}
                        loading={
                            loading
                                ? {
                                      indicator: (
                                          <Spin />
                                      ),
                                  }
                                : false
                        }
                        dataSource={
                            filteredCourses
                        }
                        columns={
                            columns
                        }
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: [
                                "10",
                                "20",
                                "50",
                                "100",
                            ],
                            showTotal: (
                                total
                            ) =>
                                `${total} course${
                                    total ===
                                    1
                                        ? ""
                                        : "s"
                                }`,
                        }}
                        scroll={{
                            x: 1500,
                        }}
                        rowSelection={{
                            selectedRowKeys,
                            onChange:
                                setSelectedRowKeys,
                            getCheckboxProps:
                                (
                                    record
                                ) => ({
                                    disabled:
                                        record.learning_status ===
                                        "approved" ||
                                        !getEnrollmentId(
                                            record
                                        ),
                                }),
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        search ||
                                        statusFilter !==
                                            "all"
                                            ? "No matching course records"
                                            : "No course approval records"
                                    }
                                />
                            ),
                        }}
                    />
                </Card>

                <div className="cayep-approval-mobile-list">
                    {loading ? (
                        <Card
                            style={{
                                borderRadius: 14,
                                textAlign:
                                    "center",
                            }}
                        >
                            <Spin />
                        </Card>
                    ) : !filteredCourses.length ? (
                        <Card
                            style={{
                                borderRadius: 14,
                            }}
                        >
                            <Empty
                                description={
                                    search ||
                                    statusFilter !==
                                        "all"
                                        ? "No matching course records"
                                        : "No course approval records"
                                }
                            />
                        </Card>
                    ) : (
                        filteredCourses.map(
                            (record) => {
                                const isApproved =
                                    record.learning_status ===
                                    "approved";

                                const rowKey =
                                    getRowKey(
                                        record
                                    );

                                const isSelected =
                                    selectedRowKeys.includes(
                                        rowKey
                                    );

                                return (
                                    <div
                                        key={
                                            rowKey
                                        }
                                        className="cayep-approval-mobile-card"
                                    >
                                        <div className="cayep-approval-mobile-card-top">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    isSelected
                                                }
                                                disabled={
                                                    isApproved ||
                                                    !getEnrollmentId(
                                                        record
                                                    )
                                                }
                                                onChange={() => {
                                                    setSelectedRowKeys(
                                                        (
                                                            current
                                                        ) =>
                                                            current.includes(
                                                                rowKey
                                                            )
                                                                ? current.filter(
                                                                      (
                                                                          key
                                                                      ) =>
                                                                          key !==
                                                                          rowKey
                                                                  )
                                                                : [
                                                                      ...current,
                                                                      rowKey,
                                                                  ]
                                                    );
                                                }}
                                            />

                                            <Avatar
                                                size={
                                                    38
                                                }
                                                icon={
                                                    <UserOutlined />
                                                }
                                                className="cayep-approval-avatar"
                                            />

                                            <div className="cayep-approval-mobile-card-info">
                                                <strong>
                                                    {getStudentName(
                                                        record
                                                    )}
                                                </strong>

                                                {getStudentUsername(
                                                    record
                                                ) && (
                                                    <span>
                                                        @
                                                        {
                                                            getStudentUsername(
                                                                record
                                                            )
                                                        }
                                                    </span>
                                                )}
                                            </div>

                                            {getStatusTag(
                                                record
                                            )}
                                        </div>

                                        <div className="cayep-approval-mobile-course">
                                            <div className="cayep-approval-mobile-course-title">
                                                {record.course_name ||
                                                    "Unnamed Course"}
                                            </div>

                                            <div className="cayep-approval-mobile-meta">
                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Facilitator
                                                    </small>

                                                    <span>
                                                        {record.facilitator_name ||
                                                            "—"}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Training Year
                                                    </small>

                                                    <span>
                                                        {getTrainingYear(
                                                            record
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Course Amount
                                                    </small>

                                                    <span>
                                                        {formatCurrency(
                                                            record.unit_price,
                                                            record.currency ||
                                                                "LRD"
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Payment
                                                    </small>

                                                    <span>
                                                        {record.payment_method
                                                            ? record.payment_method
                                                                  .replace(
                                                                      /_/g,
                                                                      " "
                                                                  )
                                                                  .replace(
                                                                      /\b\w/g,
                                                                      (
                                                                          letter
                                                                      ) =>
                                                                          letter.toUpperCase()
                                                                  )
                                                            : "—"}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Order
                                                    </small>

                                                    <span>
                                                        {record.order_id
                                                            ? `#${record.order_id}`
                                                            : "—"}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Submitted
                                                    </small>

                                                    <span>
                                                        {formatDateTime(
                                                            record.submitted_at ||
                                                                record.enrolled_at ||
                                                                record.created_at
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="cayep-approval-mobile-meta-item">
                                                    <small>
                                                        Approved
                                                    </small>

                                                    <span>
                                                        {isApproved
                                                            ? formatDateTime(
                                                                  record.approved_at
                                                              )
                                                            : "Not approved"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {!isApproved && (
                                            <div className="cayep-approval-mobile-actions">
                                                <Button
                                                    type="primary"
                                                    icon={
                                                        <CheckCircleOutlined />
                                                    }
                                                    loading={
                                                        approving
                                                    }
                                                    disabled={
                                                        !getEnrollmentId(
                                                            record
                                                        )
                                                    }
                                                    onClick={() =>
                                                        approveSingle(
                                                            record
                                                        )
                                                    }
                                                    style={{
                                                        background:
                                                            "#16805C",
                                                        borderColor:
                                                            "#16805C",
                                                    }}
                                                >
                                                    Approve Course
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        )
                    )}
                </div>
            </div>
        </>
    );
}

export default ApprovedLearningCourse;

